import { motion } from "framer-motion";

// SVG world map points (approx normalized to 1000x500 viewport)
// Türkiye is the hub. Lines radiate to Europe, ME, GCC, N. Africa, Central Asia, Global
const HUB = { x: 565, y: 195, label: "Türkiye" };
const POINTS = [
  { x: 470, y: 145, label: "Europe" },
  { x: 615, y: 220, label: "Middle East" },
  { x: 640, y: 250, label: "GCC" },
  { x: 510, y: 260, label: "North Africa" },
  { x: 720, y: 190, label: "Central Asia" },
  { x: 250, y: 240, label: "Americas" },
  { x: 820, y: 290, label: "Asia Pacific" },
];

export default function Markets() {
  return (
    <section
      id="markets"
      data-testid="markets-section"
      className="py-24 md:py-32 bg-altin-ink relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, #C5A059 1px, transparent 0)",
        backgroundSize: "40px 40px",
      }} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-divider" />
              <span className="overline">Global Reach</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-white tracking-tight">
              Markets we{" "}
              <span className="italic text-altin-gold">connect</span>.
            </h2>
            <p className="mt-6 text-white/70 font-light leading-relaxed max-w-md">
              From our Istanbul base we bridge commercial flows across seven
              strategic regions, leveraging local presence and a verified
              partner network on every continent we operate in.
            </p>

            <ul className="mt-10 space-y-3">
              {[HUB, ...POINTS].map((p) => (
                <li
                  key={p.label}
                  className="flex items-center gap-3 text-sm text-white/80"
                >
                  <span className="w-2 h-2 rounded-full bg-altin-gold" />
                  {p.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            <div className="relative aspect-[2/1] bg-altin-ink border border-white/10">
              <svg
                viewBox="0 0 1000 500"
                className="w-full h-full"
                xmlns="http://www.w3.org/2000/svg"
                data-testid="markets-map"
              >
                {/* Background world silhouette — simple stylised continents */}
                <g fill="#C5A059" opacity="0.08">
                  {/* North America */}
                  <path d="M120,160 Q150,100 230,110 Q290,120 320,160 Q330,200 310,235 Q280,260 230,255 Q175,250 150,225 Q115,200 120,160 Z" />
                  {/* South America */}
                  <path d="M270,290 Q295,275 320,295 Q335,335 320,385 Q300,420 280,415 Q260,395 260,355 Q255,315 270,290 Z" />
                  {/* Europe */}
                  <path d="M450,130 Q500,110 555,135 Q575,160 555,180 Q510,195 470,180 Q440,160 450,130 Z" />
                  {/* Africa */}
                  <path d="M500,200 Q560,205 580,260 Q585,330 555,375 Q525,400 500,380 Q480,335 475,275 Q485,220 500,200 Z" />
                  {/* Middle East */}
                  <path d="M590,200 Q625,200 650,225 Q655,260 630,275 Q605,275 590,255 Q580,225 590,200 Z" />
                  {/* Asia */}
                  <path d="M660,130 Q740,115 820,140 Q870,170 870,220 Q855,265 800,265 Q740,265 700,240 Q655,210 660,170 Q655,150 660,130 Z" />
                  {/* SE Asia / Oceania */}
                  <path d="M820,275 Q860,280 880,310 Q870,345 830,355 Q800,345 800,310 Q800,285 820,275 Z" />
                  <path d="M860,375 Q890,370 905,395 Q895,415 870,415 Q845,410 850,390 Q855,378 860,375 Z" />
                </g>

                {/* Connection lines from hub */}
                {POINTS.map((p, i) => {
                  // Curved path via midpoint
                  const mx = (HUB.x + p.x) / 2;
                  const my = Math.min(HUB.y, p.y) - 40;
                  return (
                    <motion.path
                      key={`line-${i}`}
                      d={`M ${HUB.x} ${HUB.y} Q ${mx} ${my} ${p.x} ${p.y}`}
                      stroke="#C5A059"
                      strokeWidth="1"
                      fill="none"
                      strokeDasharray="3 4"
                      initial={{ pathLength: 0, opacity: 0 }}
                      whileInView={{ pathLength: 1, opacity: 0.65 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 1.4, delay: 0.2 + i * 0.15, ease: "easeInOut" }}
                    />
                  );
                })}

                {/* Hub */}
                <motion.g
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5 }}
                  style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
                >
                  <circle cx={HUB.x} cy={HUB.y} r="14" fill="#C5A059" opacity="0.15" />
                  <circle cx={HUB.x} cy={HUB.y} r="8" fill="#C5A059" opacity="0.3" />
                  <circle cx={HUB.x} cy={HUB.y} r="4" fill="#C5A059" />
                  <text
                    x={HUB.x + 12}
                    y={HUB.y - 12}
                    fill="#FDFBF7"
                    fontSize="14"
                    fontFamily="Cormorant Garamond, serif"
                    fontStyle="italic"
                  >
                    {HUB.label}
                  </text>
                </motion.g>

                {/* Endpoints */}
                {POINTS.map((p, i) => (
                  <motion.g
                    key={`pt-${i}`}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.4, delay: 1.3 + i * 0.1 }}
                    style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                  >
                    <circle cx={p.x} cy={p.y} r="6" fill="#C5A059" opacity="0.18" />
                    <circle cx={p.x} cy={p.y} r="3" fill="#C5A059" />
                    <text
                      x={p.x + 8}
                      y={p.y - 8}
                      fill="rgba(255,255,255,0.7)"
                      fontSize="11"
                      fontFamily="Outfit, sans-serif"
                      letterSpacing="0.1em"
                    >
                      {p.label.toUpperCase()}
                    </text>
                  </motion.g>
                ))}
              </svg>
            </div>
            <p className="mt-4 text-xs text-white/40 tracking-[0.25em] uppercase">
              Headquartered in Istanbul · Active across 7 regions
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
